/**
 * SanPlumb — Alturas NPT y dimensiones de aparatos sanitarios
 *
 * Fuente: Manual PAEBA — Programa de Apoyo a la Educación Básica del Perú
 *         Módulos M1 (instalaciones sanitarias domiciliarias) y M2 (prácticas
 *         constructivas). MINEDU, 2008.
 *
 * Origen de migración:
 *   - NPT_HEIGHTS  ← legacy IS010.nptDefaults (movido a paeba.ts por CLAUDE.md §XII).
 *                    Valores convertidos de centímetros (MVP) a metros (SI/Three.js).
 *   - DIMENSIONES_APARATOS  ← CLAUDE.md §IX (no existían como constante en el MVP;
 *                              estaban hardcodeadas dentro de DEMO.sanitaries).
 *
 * UNIDAD: TODAS las distancias en metros, medidas desde NPT ±0.00 (piso terminado).
 *         NUNCA desde la losa estructural — ver CLAUDE.md §VI.
 */

import type { SanitaryType } from '../types';

// ──────────────────────────────────────────────────────────────────
// NPT_HEIGHTS — Alturas estándar de puntos de salida (en metros)
// ──────────────────────────────────────────────────────────────────

/**
 * Alturas referenciales de puntos de salida AF / AC / desagüe por aparato.
 * Todas medidas desde NPT ±0.00 (Nivel de Piso Terminado).
 *
 * El valor `alimentacion_*` aplica al ramal horizontal general (no a un punto
 * de salida específico). El valor `llave_paso_eje` es la altura del eje de la
 * válvula dentro del ensamblaje en U (sube 10 cm sobre la alimentación).
 */
export const NPT_HEIGHTS = {
  // Alimentaciones generales en muros (no son punto de uso)
  alimentacion_af: 0.3, // AF empotrada en muro
  alimentacion_ac: 0.05, // AC por contrapiso
  llave_paso_eje: 0.4, // eje de válvula en ensamblaje U

  // Inodoro
  inodoro_af: 0.15, // 10–20 cm — usar 15 cm como default
  inodoro_des: 0.01, // 1 cm sobre NPT — desagüe a nivel de piso
  inodoro_eje_pared: 0.305, // distancia del eje del inodoro al muro terminado

  // Lavatorio
  lavatorio_af: 0.575, // rango 55–60 cm — default 57.5
  lavatorio_ac: 0.575,
  lavatorio_des: 0.5, // desagüe en muro a +50 cm

  // Ducha
  ducha_llave: 1.05, // mezcladora 100–110 cm — default 105
  ducha_brazo: 2.05, // brazo de regadera 200–210 — default 205

  // Terma eléctrica (cuando está en azotea/cuarto técnico, no en baño)
  terma_af: 1.5, // entrada AF a +1.50 m
  terma_ac: 1.5, // salida AC a +1.50 m
  terma_des: 1.3, // bandeja de rebose

  // Lavadero de platos (mezcladora en pared)
  lav_platos_af: 0.975, // 95–100 cm — default 97.5
  lav_platos_ac: 0.975,
  lav_platos_des: 0.55,

  // Lavadero de ropa (prefabricado o concreto)
  lav_ropa_af: 1.025, // 100–105 cm — default 102.5
  lav_ropa_ac: 1.025,
  lav_ropa_des: 0.45, // prefabricado: +0.45 · concreto: +0.25 (variante manual)

  // Urinario
  urinario_af: 1.28, // +1.28 m al eje
  urinario_des_c_trampa: 0.55, // desagüe con trampa
  urinario_des_s_trampa: 0.35, // desagüe sin trampa
} as const;

// ──────────────────────────────────────────────────────────────────
// ESPESOR DE MURO y SEPARACIÓN DE TUBERÍAS — Routing condicional
// ──────────────────────────────────────────────────────────────────

/**
 * Constantes de espesor de muro.
 *
 * Para los lectores futuros: el espesor del muro NO limita la disposición
 * AF/AC porque ambas tuberías van en el MISMO PLANO horizontal a lo largo
 * del muro (no una delante de la otra en el espesor). Lo que el espesor
 * limita es si una tubería cabe con su cubrimiento mínimo.
 */
export const ESPESOR_MURO = {
  /** Espesor mínimo para alojar UNA tubería con cubrimiento adecuado (m). */
  MIN_PARA_TUBERIA: 0.06,
  /** Espesor de muro de ladrillo King Kong típico peruano (m). */
  KING_KONG: 0.15,
  /** Tarrajeo mínimo por cara (m). */
  TARRAJEO_POR_CARA: 0.015,
} as const;

/**
 * Separación entre tuberías AF y AC cuando van paralelas en el mismo muro
 * (Estrategia A — muro dual). Conforme IS.010 §2.3.i y la práctica peruana.
 *
 * IMPORTANTE: La separación se mide a lo LARGO del muro (horizontal), no en
 * el espesor. Disponer una tubería delante de otra dentro del espesor del
 * muro es INCORRECTO normativamente.
 */
export const SEPARACION_TUBERIAS = {
  /** Separación horizontal mínima entre generatrices (m). IS.010 §2.3.i. */
  MIN_GENERATRICES: 0.03,
  /** Separación horizontal recomendada (m). Rango 5-7 cm. */
  RECOMENDADA: 0.06,
  /** Recubrimiento mínimo de cada tubería en ranura (m). */
  RECUBRIMIENTO_MIN: 0.015,
  /** Ancho típico de ranura para una tubería (m). */
  ANCHO_RANURA: 0.04,
} as const;

/**
 * Tabla de disponibilidad de estrategias de routing según espesor de muro.
 * El engine consulta esta tabla cuando rota entre estrategias (Regenerar 3D).
 */
export const ESTRATEGIA_DISPONIBLE = {
  /** Estrategia 'muro_dual' (A) — requiere muro mínimo 6 cm (cabe la tubería). */
  MIN_MURW_MURO_DUAL: ESPESOR_MURO.MIN_PARA_TUBERIA,
  /** Estrategia 'mixta' (B) — requiere muro mínimo 6 cm (para AF). */
  MIN_MURW_MIXTA: ESPESOR_MURO.MIN_PARA_TUBERIA,
  /** Estrategia 'piso_dual' (C) — sin restricción de muro (todo va por piso). */
  MIN_MURW_PISO_DUAL: 0,
} as const;

// ──────────────────────────────────────────────────────────────────
// Convención AF/AC en muro — Offsets horizontales desde el eje
// ──────────────────────────────────────────────────────────────────

/**
 * Reglas espaciales de los dos puntos de agua en cada aparato.
 *
 * REGLA GENERAL §VI:
 *   - AF a la DERECHA del eje:  +10 cm
 *   - AC a la IZQUIERDA del eje: −10 cm
 *   - Separación total = 20 cm (cumple mínimo IS.010 §2.3.i = 15 cm)
 *
 * EXCEPCIÓN §XV.inodoro:
 *   - Inodoro AF a la IZQUIERDA: −15 cm (entrada del tanque está
 *     en la parte inferior izquierda del aparato).
 */
export const CONVENCION_PUNTOS = {
  /** Offset horizontal AF (eje + offset) en aparatos NO inodoro. */
  AF_OFFSET_HORIZONTAL: 0.1,
  /** Offset horizontal AC (eje + offset) en aparatos NO inodoro. */
  AC_OFFSET_HORIZONTAL: -0.1,
  /** Excepción inodoro: AF va a la IZQUIERDA. */
  INODORO_AF_OFFSET: -0.15,
} as const;

// ──────────────────────────────────────────────────────────────────
// DIMENSIONES_APARATOS — Geometría física de los aparatos (en metros)
// ──────────────────────────────────────────────────────────────────

/**
 * Dimensiones físicas referenciales de aparatos sanitarios (PAEBA M1).
 * Sirven como defaults cuando el usuario no especifica un modelo del catálogo.
 *
 * Convención de ejes (consistente con Three.js):
 *   - largo: dimensión en X (frontal del aparato)
 *   - ancho: dimensión en Z (lateral)
 *   - altura: dimensión en Y (vertical)
 */
export interface DimensionesAparato {
  largo: number;
  ancho: number;
  altura: number;
  /** Notas constructivas relevantes (ubicación de desagüe, conexiones, etc.). */
  nota?: string;
}

export const DIMENSIONES_APARATOS: Partial<Record<SanitaryType, DimensionesAparato>> = {
  Inodoro: {
    largo: 0.495,
    ancho: 0.375,
    altura: 0.75,
    nota: 'Desagüe a 30.5 cm del eje y de la pared terminada',
  },
  Lavatorio: {
    largo: 0.645, // total exterior — cuenco 0.52
    ancho: 0.5, // total exterior — cuenco 0.30
    altura: 0.85, // al borde superior
    nota: 'Desagüe ∅46 mm · borde a 80–85 cm NPT',
  },
  Ducha: {
    largo: 0.9, // área libre interior mínima
    ancho: 0.9,
    altura: 0.02, // plato/rejilla
    nota: 'Área libre mínima 0.9 × 0.9 m',
  },
  'Lavadero de platos': {
    largo: 0.94,
    ancho: 0.485,
    altura: 0.9,
    nota: 'Fregadero + escurridera',
  },
  'Lavadero de ropa': {
    largo: 0.6,
    ancho: 0.5, // poza 0.50 × 0.50
    altura: 0.9,
    nota: 'Variantes en concreto vs prefabricado',
  },
};

// ──────────────────────────────────────────────────────────────────
// TUBO_ABASTO — Longitud del flexible muro → aparato
// ──────────────────────────────────────────────────────────────────

export const TUBO_ABASTO = {
  /** Longitud mínima recomendada (metros). */
  MIN: 0.3,
  /** Longitud máxima recomendada (metros). */
  MAX: 0.35,
} as const;
